import { useState } from 'react';
import { UseFormProps, FormContextType } from '../types/form-types';

export const useForm = ({
  initialValues = {},
  validationRules = {},
  onSubmit
}: UseFormProps) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = (fieldName: string, value: any) => {
    const rules = validationRules[fieldName];
    if (!rules) return '';

    if (rules.required && !value) {
      return rules.requiredMessage || 'This field is required';
    }

    if (rules.minLength && value.length < rules.minLength) {
      return rules.minLengthMessage || `Minimum ${rules.minLength} characters required`;
    }

    if (rules.maxLength && value.length > rules.maxLength) {
      return rules.maxLengthMessage || `Maximum ${rules.maxLength} characters allowed`;
    }

    if (rules.pattern && !rules.pattern.test(value)) {
      return rules.patternMessage || 'Invalid format';
    }

    if (rules.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return rules.emailMessage || 'Invalid email address';
    }

    if (rules.min && Number(value) < rules.min) {
      return rules.minMessage || `Minimum value is ${rules.min}`;
    }

    if (rules.max && Number(value) > rules.max) {
      return rules.maxMessage || `Maximum value is ${rules.max}`;
    }

    if (rules.custom && !rules.custom(value, values)) {
      return rules.customMessage || 'Validation failed';
    }

    return '';
  };

  const validateAll = () => {
    const newErrors: { [key: string]: string } = {};
    Object.keys(validationRules).forEach((fieldName) => {
      const error = validate(fieldName, values[fieldName] || '');
      if (error) {
        newErrors[fieldName] = error;
      }
    });
    return newErrors;
  };

  const handleChange = (name: string, value: any) => {
    setValues((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      const error = validate(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (name: string) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validate(name, values[name] || '');
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async () => {
    const newErrors = validateAll();
    setErrors(newErrors);
    setTouched(Object.keys(validationRules).reduce((acc, key) => ({ ...acc, [key]: true }), {}));

    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      try {
        await onSubmit(values);
      } catch (error) {
        console.error('Form submission error:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleReset = () => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  };

  const formContext: FormContextType = {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    handleReset,
    setFieldValue: (name, value) => handleChange(name, value),
    setFieldError: (name, error) => setErrors((prev) => ({ ...prev, [name]: error }))
  };

  return {
    formContext
  };
};

